import { z } from 'zod';
const SIMPLE_RESPONSE_KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;
function isPlainObject(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function pathFailure(message, position) {
    return { success: false, message, position };
}
function parseBracketToken(path, start) {
    const contentStart = start + 1;
    if (path[contentStart] === ']') {
        return { success: true, token: { type: 'each' }, nextPosition: contentStart + 1 };
    }
    if (path[contentStart] === '"') {
        let cursor = contentStart + 1;
        let escaped = false;
        while (cursor < path.length) {
            const character = path[cursor];
            if (!escaped && character === '"')
                break;
            if (!escaped && character === '\\') {
                escaped = true;
            }
            else {
                escaped = false;
            }
            cursor += 1;
        }
        if (cursor >= path.length || path[cursor] !== '"') {
            return pathFailure('Quoted response key is not terminated', start);
        }
        if (path[cursor + 1] !== ']') {
            return pathFailure('Quoted response key must end with ]', cursor + 1);
        }
        try {
            const key = JSON.parse(path.slice(contentStart, cursor + 1));
            if (typeof key !== 'string') {
                return pathFailure('Bracket response keys must be JSON strings', contentStart);
            }
            return {
                success: true,
                token: { type: 'property', key },
                nextPosition: cursor + 2,
            };
        }
        catch {
            return pathFailure('Quoted response key contains an invalid JSON escape', contentStart);
        }
    }
    const closingBracket = path.indexOf(']', contentStart);
    if (closingBracket === -1) {
        return pathFailure('Response index is not terminated', start);
    }
    const indexText = path.slice(contentStart, closingBracket);
    if (!/^(0|[1-9]\d*)$/.test(indexText)) {
        return pathFailure('Response brackets must contain an index, a JSON key, or be empty', contentStart);
    }
    const index = Number(indexText);
    if (!Number.isSafeInteger(index)) {
        return pathFailure('Response index is too large', contentStart);
    }
    return {
        success: true,
        token: { type: 'index', index },
        nextPosition: closingBracket + 1,
    };
}
/** Parses a response path without the leading `response` namespace. */
export function parseResponsePath(path) {
    if (path.length === 0)
        return pathFailure('A response field or path is required', 0);
    const tokens = [];
    let cursor = 0;
    let expectsSegment = true;
    while (cursor < path.length) {
        const character = path[cursor];
        if (character === '.') {
            if (expectsSegment)
                return pathFailure('Response path contains an empty segment', cursor);
            expectsSegment = true;
            cursor += 1;
            continue;
        }
        if (character === '[') {
            if (expectsSegment && tokens.length > 0) {
                return pathFailure('Response path contains an empty segment', cursor);
            }
            const bracket = parseBracketToken(path, cursor);
            if (!bracket.success)
                return bracket;
            tokens.push(bracket.token);
            cursor = bracket.nextPosition;
            expectsSegment = false;
            continue;
        }
        if (!expectsSegment) {
            return pathFailure('Response path segments must be separated by . or brackets', cursor);
        }
        const segmentStart = cursor;
        while (cursor < path.length && path[cursor] !== '.' && path[cursor] !== '[')
            cursor += 1;
        const key = path.slice(segmentStart, cursor);
        if (!SIMPLE_RESPONSE_KEY_PATTERN.test(key)) {
            return pathFailure('Response keys with punctuation, spaces, or leading digits must use bracket quotes', segmentStart);
        }
        tokens.push({ type: 'property', key });
        expectsSegment = false;
    }
    if (expectsSegment)
        return pathFailure('Response path cannot end with .', path.length - 1);
    return { success: true, tokens };
}
export function formatResponsePath(tokens) {
    let path = '';
    for (const token of tokens) {
        if (token.type === 'each') {
            path += '[]';
            continue;
        }
        if (token.type === 'index') {
            path += `[${token.index}]`;
            continue;
        }
        if (SIMPLE_RESPONSE_KEY_PATTERN.test(token.key)) {
            path += path.length === 0 ? token.key : `.${token.key}`;
        }
        else {
            path += `[${JSON.stringify(token.key)}]`;
        }
    }
    return path;
}
/** Parses one exact `{{response...}}` projection leaf. */
export function parseResponseReference(value) {
    if (!value.startsWith('{{') || !value.endsWith('}}')) {
        return { success: false, message: 'Projection leaves must be one exact {{response...}} reference' };
    }
    const expression = value.slice(2, -2);
    if (!expression.startsWith('response.') && !expression.startsWith('response[')) {
        return { success: false, message: 'Projection leaves must be one exact {{response...}} reference' };
    }
    const suffix = expression.slice('response'.length);
    const path = suffix[0] === '.' ? suffix.slice(1) : suffix;
    const parsedPath = parseResponsePath(path);
    if (!parsedPath.success) {
        return { success: false, message: parsedPath.message };
    }
    return {
        success: true,
        path: formatResponsePath(parsedPath.tokens),
        tokens: parsedPath.tokens,
    };
}
export function responseTemplateForPath(path) {
    const parsed = parseResponsePath(path);
    if (!parsed.success)
        return null;
    const canonicalPath = formatResponsePath(parsed.tokens);
    const separator = canonicalPath.startsWith('[') ? '' : '.';
    return `{{response${separator}${canonicalPath}}}`;
}
export function responsePathFromTemplate(value) {
    const parsed = parseResponseReference(value);
    return parsed.success ? parsed.path : null;
}
function validateProjectionNode(node, path, ancestors, issues) {
    if (typeof node === 'string') {
        const reference = parseResponseReference(node);
        if (!reference.success) {
            issues.push({ code: 'invalid_expression', path, message: reference.message });
        }
        return;
    }
    if (Array.isArray(node)) {
        if (node.length === 0) {
            issues.push({
                code: 'empty_container',
                path,
                message: 'Nested projection arrays cannot be empty',
            });
            return;
        }
        if (ancestors.has(node)) {
            issues.push({ code: 'invalid_shape', path, message: 'Projection values cannot be circular' });
            return;
        }
        ancestors.add(node);
        for (let index = 0; index < node.length; index += 1) {
            validateProjectionNode(node[index], [...path, index], ancestors, issues);
        }
        ancestors.delete(node);
        return;
    }
    if (!isPlainObject(node)) {
        issues.push({
            code: 'invalid_shape',
            path,
            message: 'Projection values must be response references, objects, or arrays',
        });
        return;
    }
    const entries = Object.entries(node);
    if (entries.length === 0) {
        issues.push({ code: 'empty_container', path, message: 'Nested projection objects cannot be empty' });
        return;
    }
    if (ancestors.has(node)) {
        issues.push({ code: 'invalid_shape', path, message: 'Projection values cannot be circular' });
        return;
    }
    ancestors.add(node);
    for (const [key, value] of entries) {
        validateProjectionNode(value, [...path, key], ancestors, issues);
    }
    ancestors.delete(node);
}
export function validateToolResponseProjection(value) {
    if (!isPlainObject(value)) {
        return [
            {
                code: 'invalid_root',
                path: [],
                message: 'Response mapping must be a JSON object',
            },
        ];
    }
    const issues = [];
    const ancestors = new WeakSet();
    ancestors.add(value);
    for (const [key, node] of Object.entries(value)) {
        validateProjectionNode(node, [key], ancestors, issues);
    }
    return issues;
}
export const toolResponseProjectionSchema = z
    .custom(value => isPlainObject(value), {
    message: 'Response mapping must be a JSON object',
})
    .superRefine((value, context) => {
    for (const issue of validateToolResponseProjection(value)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: issue.path,
            message: issue.message,
            params: { projectionIssueCode: issue.code },
        });
    }
});
function resolveResponsePath(value, tokens, tokenIndex = 0) {
    if (tokenIndex === tokens.length)
        return { status: 'resolved', value };
    const token = tokens[tokenIndex];
    if (!token)
        return { status: 'resolved', value };
    if (token.type === 'property') {
        if (!isPlainObject(value))
            return { status: 'failure' };
        if (!Object.prototype.hasOwnProperty.call(value, token.key))
            return { status: 'missing' };
        return resolveResponsePath(value[token.key], tokens, tokenIndex + 1);
    }
    if (token.type === 'index') {
        if (!Array.isArray(value))
            return { status: 'failure' };
        if (token.index >= value.length)
            return { status: 'missing' };
        return resolveResponsePath(value[token.index], tokens, tokenIndex + 1);
    }
    if (!Array.isArray(value))
        return { status: 'failure' };
    let issueCode;
    const projected = value.map(item => {
        const itemResolution = resolveResponsePath(item, tokens, tokenIndex + 1);
        if (itemResolution.status === 'resolved') {
            if (itemResolution.issueCode === 'resolution_failure')
                issueCode = 'resolution_failure';
            else if (itemResolution.issueCode === 'missing_path' && issueCode === undefined) {
                issueCode = 'missing_path';
            }
            return itemResolution.value;
        }
        if (itemResolution.status === 'failure')
            issueCode = 'resolution_failure';
        else if (issueCode === undefined)
            issueCode = 'missing_path';
        return null;
    });
    return { status: 'resolved', value: projected, issueCode };
}
function resolutionIssue(code, outputPath, responsePath) {
    return {
        code,
        outputPath,
        responsePath,
        message: code === 'missing_path'
            ? `Response path "${responsePath}" was not present`
            : `Response path "${responsePath}" could not be resolved against this value`,
    };
}
function resolveProjectionNode(node, response, outputPath) {
    if (typeof node === 'string') {
        const reference = parseResponseReference(node);
        if (!reference.success) {
            throw new Error(reference.message);
        }
        const resolved = resolveResponsePath(response, reference.tokens);
        if (resolved.status !== 'resolved') {
            const code = resolved.status === 'missing' ? 'missing_path' : 'resolution_failure';
            return {
                included: false,
                issues: [resolutionIssue(code, outputPath, reference.path)],
            };
        }
        return {
            included: true,
            value: resolved.value,
            issues: resolved.issueCode
                ? [resolutionIssue(resolved.issueCode, outputPath, reference.path)]
                : [],
        };
    }
    if (Array.isArray(node)) {
        const issues = [];
        const value = node.map((child, index) => {
            const resolved = resolveProjectionNode(child, response, [...outputPath, index]);
            issues.push(...resolved.issues);
            return resolved.included ? resolved.value : null;
        });
        return { included: true, value, issues };
    }
    const issues = [];
    const value = {};
    for (const [key, child] of Object.entries(node)) {
        const resolved = resolveProjectionNode(child, response, [...outputPath, key]);
        issues.push(...resolved.issues);
        if (resolved.included) {
            // Assignment invokes the legacy __proto__ setter for a partner-authored output key.
            Object.defineProperty(value, key, {
                configurable: true,
                enumerable: true,
                value: resolved.value,
                writable: true,
            });
        }
    }
    return { included: true, value, issues };
}
/** Resolves a validated projection while preserving native response values. */
export function resolveToolResponseProjection(projection, response) {
    if (projection == null || Object.keys(projection).length === 0) {
        return { value: response, issues: [] };
    }
    const validationIssues = validateToolResponseProjection(projection);
    if (validationIssues.length > 0) {
        throw new Error(validationIssues.map(issue => issue.message).join('; '));
    }
    const resolved = resolveProjectionNode(projection, response, []);
    return { value: resolved.value, issues: resolved.issues };
}
export var ResponseProjectionIssueCodeEnum;
(function (ResponseProjectionIssueCodeEnum) {
    ResponseProjectionIssueCodeEnum["INVALID_SHAPE"] = "invalid_shape";
    ResponseProjectionIssueCodeEnum["INVALID_EXPRESSION"] = "invalid_expression";
    ResponseProjectionIssueCodeEnum["MISSING_PATH"] = "missing_path";
    ResponseProjectionIssueCodeEnum["RESOLUTION_FAILURE"] = "resolution_failure";
})(ResponseProjectionIssueCodeEnum || (ResponseProjectionIssueCodeEnum = {}));
export var ResponseProjectionIssueSeverityEnum;
(function (ResponseProjectionIssueSeverityEnum) {
    ResponseProjectionIssueSeverityEnum["ERROR"] = "error";
    ResponseProjectionIssueSeverityEnum["WARNING"] = "warning";
})(ResponseProjectionIssueSeverityEnum || (ResponseProjectionIssueSeverityEnum = {}));
export const responseProjectionIssueSchema = z.object({
    severity: z.nativeEnum(ResponseProjectionIssueSeverityEnum),
    code: z.nativeEnum(ResponseProjectionIssueCodeEnum),
    message: z.string(),
    outputPath: z.array(z.union([z.string(), z.number()])).optional(),
    responsePath: z.string().optional(),
});
export const responseProjectionPreviewRequestSchema = z.object({
    sampleResponse: z.unknown().optional(),
    responseMapping: z.unknown().nullable().optional(),
});
export const responseProjectionPreviewSchema = z.object({
    mapped: z.unknown().optional(),
    passthrough: z.boolean(),
    issues: z.array(responseProjectionIssueSchema),
});
//# sourceMappingURL=tool-response-projection.js.map